import React from 'react';
import { BadgeCheck, Sparkles } from 'lucide-react';
import type { Class } from '../types/api';
import PaymentsNote from './PaymentsNote';
import UpgradeButton from './UpgradeButton';
import { formatDailyTime, formatDate } from '../utils/format';

// The selected class's plan (docs/REQUIREMENTS.md PLAN-3, PLAN-4, PLAN-13): an evaluation class shows its limit and how
// to activate it (Paddle checkout, PLAN-6); a paid class shows its period, seats and each student's daily class time.
const ClassPlanBanner: React.FC<{ cls: Class }> = ({ cls }) => {
  const count = cls.student_count ?? cls.students?.length ?? 0;
  const limit = cls.student_limit ?? 0;
  if (cls.plan === 'class') {
    const daily = cls.seconds_per_student_per_day ?? 0;
    return (
      <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-emerald-200 dark:border-emerald-900/70 bg-emerald-50 dark:bg-emerald-950/30 px-4 py-3 text-sm text-emerald-900 dark:text-emerald-100">
        <span className="inline-flex items-center gap-1.5 font-semibold"><BadgeCheck className="w-4 h-4" /> Class plan</span>
        {cls.plan_period_end && <span>until {formatDate(cls.plan_period_end)}</span>}
        <span>{count} / {limit} students</span>
        {daily > 0 && <span>{formatDailyTime(daily)} of simulation a day for each student</span>}
      </div>
    );
  }
  const full = count >= limit;
  return (
    <div className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-200 dark:border-amber-900/70 bg-amber-50 dark:bg-amber-950/30 px-4 py-3.5 text-sm text-amber-900 dark:text-amber-100 sm:flex-row sm:items-center sm:gap-6">
      <div className="flex-1 min-w-0">
        <p className="font-semibold">Evaluation · {count} / {limit} students{full ? ' (full)' : ''}</p>
        <p className="mt-0.5 text-amber-800 dark:text-amber-200/80">
          Try everything with up to {limit} students. To teach your class: the Class plan, €890 per semester, up to 30
          students, each with 1 hour of simulation a day for the class's lessons.
        </p>
        <PaymentsNote className="mt-1.5 text-xs text-amber-800/80 dark:text-amber-200/60" />
      </div>
      <UpgradeButton
        plan="class"
        forClass={{ id: cls.id, name: cls.name }}
        className="btn shrink-0 bg-amber-600 text-white shadow-sm hover:bg-amber-700 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
        // PLAN-18: until online payment is on, a free month of the Class plan for this class instead.
        trial={{
          className: 'shrink-0 sm:max-w-[17rem]',
          noteClassName: 'text-amber-800 dark:text-amber-200/80',
          icon: <Sparkles />,
        }}
      >
        <Sparkles /> Activate this class
      </UpgradeButton>
    </div>
  );
};

export default ClassPlanBanner;
